import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoadingPromise = null;

function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (scriptLoadingPromise) return scriptLoadingPromise;

    scriptLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return scriptLoadingPromise;
}

const Turnstile = forwardRef(({ onVerify }, ref) => {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
            }
        }
    }));

    useEffect(() => {
        let mounted = true;

        loadTurnstileScript().then(() => {
            if (!mounted || !containerRef.current || !window.turnstile) return;

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
                callback: (token) => onVerify(token),
                'expired-callback': () => onVerify(''),
                'error-callback': () => onVerify('')
            });
        });

        return () => {
            mounted = false;
            if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.remove(widgetIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <div ref={containerRef} className="turnstile-widget" />;
});

export default Turnstile;
